/**
 * events-data.js
 * -------------------------------------------------------
 * Single source of truth for every event VerdeMobility
 * attends/exhibits at. The homepage slider (EventsConferences.jsx)
 * and the detail page (EventDetail/index.jsx) both read from
 * here — when a future /events listing page is built, it reads
 * from here too, so there's never more than one place to update
 * an event.
 *
 * Add a new event by adding an object to this array. `slug` is
 * required and must be unique — it becomes the URL:
 * /events/:slug
 *
 * Fields:
 * - slug          (required) URL segment, e.g. "prawaas-5-0"
 * - name          (required) Event name
 * - date          (required) Display date, e.g. "12–14 Feb 2026"
 * - location      (required) City, Country
 * - tag           (required) "Exhibitor" | "Conference" | "Speaker"
 * - boothNumber   (optional) Only shown when tag === "Exhibitor"
 * - image         (required) Hero/card image — placeholder for now,
 *                  swap for a real photo the same way Banner.jsx
 *                  does elsewhere (local import) whenever you have one:
 *                    import eventPrawaas from '../../Assets/events/prawaas-5.webp';
 *                    ...
 *                    image: eventPrawaas,
 * - brochure      (optional) PDF/link URL — shows a Download Brochure button
 * - about         (optional) Paragraph(s) describing the event / why VerdeMobility is there
 * - showcase      (optional) Array of strings — what VerdeMobility is bringing/demoing
 * - gallery       (optional) Array of { src, alt } — photos for the Gallery section
 * - team          (optional) Array of { name, role, photo } — who from VerdeMobility
 *                  will be at (or was at) this event. Leave out entirely and the
 *                  Team section just doesn't render.
 * - schedule      (optional) Array of { day, items: [{ time, activity }] } —
 *                  a day-by-day agenda. Leave out and the Schedule section
 *                  doesn't render.
 * - faq           (optional) Array of { q, a } — shown as an accordion.
 *                  Leave out and the FAQ section doesn't render.
 * - venue         (optional) Full venue address — used to auto-build the
 *                  embedded Google Map (no API key needed for the basic embed).
 *                  Leave it out and the Map section doesn't render, unless
 *                  `mapUrl` is set instead.
 * - mapUrl        (optional) A specific embed URL from Google Maps itself —Takes priority over
 *                  `venue` when both are set — use this when the plain
 *                  address search doesn't land on the right spot (new
 *                  buildings, ambiguous venue names, etc.). Leave both
 *                  `venue` and `mapUrl` out and the Map section just
 *                  doesn't render.
 * - isPast        (optional) true once the event has happened — detail page adjusts CTA copy
 */

import bannerImage1 from '../../Assets/events/bannerIIEVShow2026.webp';
import bannerImage2 from '../../Assets/events/bannerPrawaas.webp';

import listingBanner1 from '../../Assets/events/dpbannerPrawaas.webp';
import listingBanner2 from '../../Assets/events/dpbannerIIEVShow2026.webp';


import prawaasimg1 from '../../Assets/events/prawaasimg1.webp';
import prawaasimg2 from '../../Assets/events/prawaasimg2.webp';
import prawaasimg3 from '../../Assets/events/prawaasimg3.webp';
import prawaasimg4 from '../../Assets/events/prawaasimg4.webp';
import prawaasimg5 from '../../Assets/events/prawaasimg5.webp';
import prawaasimg6 from '../../Assets/events/prawaasimg6.webp';

import iievimg1 from '../../Assets/events/iievimg1.webp';
import iievimg2 from '../../Assets/events/iievimg2.webp';
import iievimg3 from '../../Assets/events/iievimg3.webp';
import iievimg4 from '../../Assets/events/iievimg4.webp';
import iievimg5 from '../../Assets/events/iievimg5.webp';
 
export const events = [
  // Paste this object into the `events` array in EventsData.js
{
    slug: 'india-international-ev-show',
    name: 'India International EV Show',
    boldText: 'EV Show',
    date: '2-4 October 2026',
    location: 'Pune, Maharashtra, India',
    tag: 'Exhibitor',
    boothNumber: 'B2', 
    image: bannerImage1,  
     listingBanner: listingBanner2,
    venue: 'Auto Cluster Exhibition Centre, Pimpri-Chinchwad, Pune, MH',
    about:
        'India International EV Show (IIEV Show) is a leading exhibition for the electric vehicle and energy storage industry, bringing together EV manufacturers, technology providers, charging infrastructure companies, industry experts, and decision-makers to explore the latest innovations and opportunities in e-mobility.',
    showcase: [
        'AC Chargers',
        'DC Chargers',
        'CMS',
        'AC Charger Development Kit',
        'Controllers',
    ],
     gallery: [
      { src: iievimg1, alt: 'VerdeMobility booth at India International EV Show' },
      { src: iievimg2, alt: 'VerdeMobility booth at India International EV Show' },
      { src: iievimg3, alt: 'VerdeMobility booth at India International EV Show' },
      { src: iievimg4, alt: 'VerdeMobility booth at India International EV Show' },
      { src: iievimg5, alt: 'VerdeMobility booth at India International EV Show' },
    ],
    team: [
        { name: 'Vimal Patel', role: 'Marketing Lead', photo: 'https://placehold.co/300x300?text=Photo' },  
        { name: 'Ravi Rathod', role: 'Go-To-Market Executive', photo: 'https://placehold.co/300x300?text=Photo' },  
    ],
    schedule: [
        {
            day: 'Day 1',
            items: [
                { time: '10:00 AM - 6:00 PM', activity: 'Booth open' },
                { time: '02:00 PM', activity: 'Live CMS walkthrough' },
            ],
        },
        {
            day: 'Day 2',
            items: [{ time: '10:00 AM - 6:00 PM', activity: 'Booth open' }],
        },
        {
            day: 'Day 3',
            items: [{ time: '10:00 AM - 6:00 PM', activity: 'Booth open' }],
        },
    ],
    faq: [
        // {
        //     q: 'Do I need to register to visit the booth?',
        //     a: 'No registration is required to visit our booth — just stop by B2 during exhibition hours.',
        // },
        // {
        //     q: 'Can I book a meeting slot in advance?',
        //     a: "Yes — reach out through our Get in Touch page and mention the India International EV Show, and we'll set up a time to meet at the venue.",
        // },
        // {
        //     q: 'Will the Charging Management Solution be demoed live?',
        //     a: 'Yes, we are running live CMS walkthroughs at 2:00 PM on Day 1 — see the schedule above for the full agenda.',
        // },
    ],
    isPast: true,
},
  

  {
    slug: 'prawaas-5-0',
    name: 'Prawaas 5.0',
    boldText: 'Prawaas',
    date: '9–11 July 2026',
    location: 'Gandhinagar, Gujarat, India',
    tag: 'Exhibitor',
    boothNumber: 'B10, Hall 2',
    category: 'Events and Exhibitions',
    image: bannerImage2,
    listingBanner: listingBanner1,
    brochure: '#',
    venue: 'Helipad Exhibition Centre (HEC), Gandhinagar',
    mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4511.52599546863!2d72.64903362601203!3d23.224520858760435!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395c2ba4be18e22f%3A0xaff4f38552dc6e53!2sHEC%20Exhibition%20Centre!5e1!3m2!1sen!2sin!4v1791452558184!5m2!1sen!2sin', // TODO: add Bing/Google Maps embed URL
    about:
      'Prawaas 5.0 is India’s flagship multimodal transport show, bringing together public transport operators, OEMs, technology providers, policymakers, and mobility stakeholders to explore innovations and solutions for safe, smart, and sustainable passenger mobility.',
    showcase: ['AC Chargers', 'DC Chargers', 'Controllers'],
    gallery: [
      { src: prawaasimg1, alt: 'VerdeMobility booth at Prawaas 5.0' },
      { src: prawaasimg2, alt: 'VerdeMobility booth at Prawaas 5.0' },
      { src: prawaasimg3, alt: 'VerdeMobility booth at Prawaas 5.0' },
      { src: prawaasimg4, alt: 'VerdeMobility booth at Prawaas 5.0' },
      { src: prawaasimg5, alt: 'VerdeMobility booth at Prawaas 5.0' },
    ],
    team: [
      { name: 'Vimal Patel', role: 'Marketing Lead', photo: 'https://placehold.co/300x300/16a34a/ffffff?text=Photo' },
      { name: 'Harshit Jadav', role: '', photo: 'https://placehold.co/300x300/16a34a/ffffff?text=Photo' },
      { name: 'Nikunj Patel', role: '', photo: 'https://placehold.co/300x300/16a34a/ffffff?text=Photo' },
      { name: 'Sagar Mistry', role: '', photo: 'https://placehold.co/300x300/16a34a/ffffff?text=Photo' },
    ],
    schedule: [
      {
        day: 'Day 1 — 9 Jul',
        items: [
          { time: '09:30 AM', activity: 'Exhibition opens' },
          { time: '05:30 PM', activity: 'Exhibition closes' },
        ],
      },
      {
        day: 'Day 2 — 10 Jul',
        items: [
          { time: '09:30 AM', activity: 'Exhibition opens' },
          { time: '05:30 PM', activity: 'Exhibition closes' },
        ],
      },
      {
        day: 'Day 3 — 11 Jul',
        items: [
          { time: '09:30 AM', activity: 'Exhibition opens' },
          { time: '05:30 PM', activity: 'Exhibition closes' },
        ],
      },
    ],
    faq: [
      // {
      //   q: 'Do I need to register to visit the booth?',
      //   a: 'No registration is required to visit our booth — just stop by E-27 during exhibition hours.',
      // },
      // {
      //   q: 'Can I book a meeting slot in advance?',
      //   a: "Yes — reach out through our Get in Touch page and mention Prawaas 5.0, and we'll set up a time to meet at the venue.",
      // },
       
    ],
    isPast: true,
  },
 
 
 
  
];

export function getEventBySlug(slug) {
  return events.find((event) => event.slug === slug);
}